import { StatsInfo } from "src/components/compounds/GameStats/stat-info-type";
import { GridItem, PlayerWithLinkColumn } from "../common-columns";
import {
  fourDigitNumberFormatter,
  percentFormatter,
  decimalFormatter,
  inningsFormatter,
  eventDateFormatter,
} from "../formatters";

export interface PitchingAdvancedormattedRow {
  player: {
    id: string;
    teamID: string;
    name: string;
    status: string;
  };
  BF: number;
  "P/BF": number;
  "P/IP": number;
  "<3%": number;
  LOO: number;
  "1ST2OUT": number;
  "123INN": number;
  "<13": number;
  FIP: number;
  "S%": number;
  "FPS%": number;
  "FPSO%": number;
  "FPSW%": number;
  "FPSH%": number;
  "BB/INN": number;
  "0BBINN": number;
  BBS: number;
  LOBB: number;
  LOBBS: number;
  "SM%": number;
  "K/BF": number;
  "K/BB": number;
  "WEAK%": number;
  "HHB%": number;
  "GO/AO": number;
  HR: number;
  "LD%": number;
  "FB%": number;
  "GB%": number;
  BABIP: number;
  "BA/RISP": number;
}

export const pitchingAdvancedGridDataFormatted = (grid: GridItem[]): PitchingAdvancedormattedRow[] => {
  return grid
    .filter(({ defense }) => defense)
    .map(({ person_id, team_id, name, eventID, date, offense, defense }) => {
      return {
        player: {
          id: person_id,
          teamID: team_id,
          name,
          eventID,
          status: "active",
        },
        // Read by the per-game Date column; undefined (and ignored) in season/box-score views.
        event: date,
        IP: defense.ip,
        BF: defense.bf,
        "P/BF": defense["P/BF"],
        "P/IP": defense["P/IP"],
        "<3%": defense["<3%"],
        LOO: defense.loo,
        "1ST2OUT": defense["1ST2OUT"],
        "123INN": defense["123INN"],
        "<13": defense["<13"],
        FIP: defense.fip,
        "S%": defense["S%"],
        "FPS%": defense["FPS%"],
        "FPSO%": defense["FPSO%"],
        "FPSW%": defense["FPSW%"],
        "FPSH%": defense["FPSH%"],
        "BB/INN": defense["BB/INN"],
        "0BBINN": defense["0BBINN"],
        BBS: defense.bbs,
        LOBB: defense.lobb,
        LOBBS: defense.lobbs,
        "SM%": defense["SM%"],
        "K/BF": defense["K/BF"],
        "K/BB": defense["K/BB"],
        "WEAK%": defense["WEAK%"],
        "HHB%": defense["HARD%"],
        "GO/AO": defense["GO/AO"],
        HR: defense.hr,
        "LD%": defense["LND%"],
        "FB%": defense["FB%"],
        "GB%": defense["GB%"],
        BABIP: defense.babip,
        "BA/RISP": defense["BA/RISP"],
      };
    });
};

export const CorePitchingAdvanced: StatsInfo[] = [
  { name: "BF", field: "BF", def: "Total batters faced" },
  {
    name: "P/IP",
    field: "P/IP",
    def: "Pitches per inning",
    valueFormatter: decimalFormatter(1),
  },
  {
    name: "P/BF",
    field: "P/BF",
    def: "Pitches per batter faced",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "<3%",
    field: "<3%",
    def: "% of batters on or out in three pitches or less",
    valueFormatter: percentFormatter,
  },
  {
    name: "LOO",
    field: "LOO",
    def: "Leadoff out (1st batter of inning)",
  },
  {
    name: "1ST2OUT",
    field: "1ST2OUT",
    def: "Innings with 1st 2 batters out",
  },
  { name: "123INN", field: "123INN", def: "1-2-3 Innings" },
  { name: "<13", field: "<13", def: "Innings of 13 pitches or fewer" },
  {
    name: "FIP",
    field: "FIP",
    def: "Fielding Independent Pitching",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "S%", field: "S%", def: "Strike percentage", valueFormatter: percentFormatter },
  {
    name: "FPS%",
    field: "FPS%",
    def: "First pitch strike percentage",
    valueFormatter: percentFormatter,
  },
  {
    name: "FPSO%",
    field: "FPSO%",
    def: "% of FPS at-bats that result in an out",
    valueFormatter: percentFormatter,
  },
  {
    name: "FPSW%",
    field: "FPSW%",
    def: "% of FPS at-bats that result in a walk",
    columnProps: { valueFormatter: percentFormatter },
  },
  {
    name: "FPSH%",
    field: "FPSH%",
    def: "% of FPS at-bats that result in a hit",
    valueFormatter: percentFormatter,
  },
  {
    name: "BB/INN",
    field: "BB/INN",
    def: "Walks per inning",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "0BBINN", field: "0BBINN", def: "Zero-walk innings" },
  { name: "BBS", field: "BBS", def: "Walks that score" },
  { name: "LOBB", field: "LOBB", def: "Leadoff walk (1st batter of inning)" },
  { name: "LOBBS", field: "LOBBS", def: "Leadoff walk that scored (1st batter of inning)" },
  {
    name: "SM%",
    field: "SM%",
    def: "% of total pitches that are swings and misses",
    valueFormatter: percentFormatter,
  },
  {
    name: "K/BF",
    field: "K/BF",
    def: "Strikeouts per batter faced",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "K/BB",
    field: "K/BB",
    def: "Strikeouts per walk",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "WEAK%",
    field: "WEAK%",
    def: "% of batted balls weakly hit (fly balls and ground balls)",
    valueFormatter: percentFormatter,
  },
  {
    name: "HHB%",
    field: "HHB%",
    def: "% of batted balls that are line drives or hard ground balls",
    valueFormatter: percentFormatter,
  },
  {
    name: "GO/AO",
    field: "GO/AO",
    def: "Ratio of ground outs to air outs",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "HR", field: "HR", def: "Home runs allowed" },
  { name: "LD%", field: "LD%", def: "Line drive percentage", valueFormatter: percentFormatter },
  { name: "FB%", field: "FB%", def: "Fly ball percentage", valueFormatter: percentFormatter },
  {
    name: "GB%",
    field: "GB%",
    def: "% of all batted balls hit on the ground",
    valueFormatter: percentFormatter,
  },
  {
    name: "BABIP",
    field: "BABIP",
    def: "Opponent batting average on balls in play",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "BA/RISP",
    field: "BA/RISP",
    def: "Opponent batting average with runners in scoring position",
    valueFormatter: fourDigitNumberFormatter,
  },
];

export const PitchingAdvanced: StatsInfo[] = (
  [
    PlayerWithLinkColumn,
    {
      name: "IP",
      field: "IP",
      def: "Innings pitched",
      sort: "desc",
      valueFormatter: inningsFormatter,
    },
  ] as StatsInfo[]
).concat(CorePitchingAdvanced);

const PitchCountToLastBatter: StatsInfo = {
  name: "LBFP#",
  field: "LBFP#",
  def: "Pitch count number of first pitch to last batter faced",
};

const EventPitchingAdvanced: StatsInfo[] = (
  [
    {
      name: "Date",
      field: "event",
      valueFormatter: eventDateFormatter,
      columnProps: {
        width: 100,
        pinned: true,
        cellStyle: { textAlign: "left" },
        sort: "asc",
      },
    },
    {
      name: "OPPONENT",
      field: "opponent",
      columnProps: {
        width: 260,
        cellStyle: { textAlign: "left" },
        headerClass: "left-aligned-column-header",
      },
    },
    {
      name: "IP",
      field: "IP",
      def: "Innings pitched",
      valueFormatter: inningsFormatter,
    },
  ] as StatsInfo[]
).concat(CorePitchingAdvanced);

EventPitchingAdvanced.splice(6, 0, PitchCountToLastBatter);
export { EventPitchingAdvanced };

export const GamePitchingAdvanced: StatsInfo[] = [
  PlayerWithLinkColumn,
  {
    name: "IP",
    field: "IP",
    def: "Innings pitched",
    valueFormatter: inningsFormatter,
    columnProps: { sort: "desc" },
  },
  { name: "BF", field: "BF", def: "Total batters faced" },
  { name: "P/IP", field: "P/IP", def: "Pitches per inning", valueFormatter: decimalFormatter(1) },
  {
    name: "P/BF",
    field: "P/BF",
    def: "Pitches per batter faced",
    valueFormatter: fourDigitNumberFormatter,
  },
  PitchCountToLastBatter,
  {
    name: "<3",
    field: "<3",
    def: "Batters on or out in three pitches or less",
  },
  {
    name: "LOO",
    field: "LOO",
    def: "Leadoff out (1st batter of inning)",
  },
  {
    name: "1ST2OUT",
    field: "1ST2OUT",
    def: "Innings with 1st 2 batters out",
  },
  { name: "123INN", field: "123INN", def: "1-2-3 Innings" },
  { name: "<13", field: "<13", def: "Innings of 13 pitches or fewer" },
  { name: "S%", field: "S%", def: "Strike percentage", valueFormatter: percentFormatter },
  {
    name: "FPS%",
    field: "FPS%",
    def: "First pitch strike percentage",
    valueFormatter: percentFormatter,
  },
  {
    name: "FPSO%",
    field: "FPSO%",
    def: "% of FPS at-bats that result in an out",
    valueFormatter: percentFormatter,
  },
  {
    name: "FPSW%",
    field: "FPSW%",
    def: "% of FPS at-bats that result in a walk",
    valueFormatter: percentFormatter,
  },
  {
    name: "FPSH%",
    field: "FPSH%",
    def: "% of FPS at-bats that result in a hit",
    valueFormatter: percentFormatter,
  },
  {
    name: "BB/INN",
    field: "BB/INN",
    def: "Walks per inning",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "0BBINN", field: "0BBINN", def: "Zero-walk innings" },
  { name: "BBS", field: "BBS", def: "Walks that score" },
  { name: "LOBB", field: "LOBB", def: "Leadoff walk (1st batter of inning)" },
  { name: "LOBBS", field: "LOBBS", def: "Leadoff walk that scored (1st batter of inning)" },
  { name: "SM", field: "SM", def: "Opposing batter swings-and-misses" },
  { name: "SW", field: "SW", def: "Total pitches batters swung at" },
  {
    name: "K/BB",
    field: "K/BB",
    def: "Strikeouts per walk",
    valueFormatter: fourDigitNumberFormatter,
  },
  { name: "WHB", field: "WHB", def: "Number of batted balls weakly hit (fly balls and ground balls)" },
  { name: "HHB", field: "HHB", def: "Number of batted balls that are line drives or hard ground balls" },
  { name: "GO", field: "GO", def: "Ground outs" },
  { name: "AO", field: "AO", def: "Air outs (fly outs)" },
  { name: "LD", field: "LD", def: "Line drives" },
  { name: "FB", field: "FLB", def: "Fly balls" },
  { name: "GB", field: "GB", def: "Total batted balls hit on the ground" },
  { name: "HR", field: "HR", def: "Home runs allowed" },
  {
    name: "BABIP",
    field: "BABIP",
    def: "Opponent batting average on balls in play",
    valueFormatter: fourDigitNumberFormatter,
  },
  {
    name: "BA/RISP",
    field: "BA/RISP",
    def: "Opponent batting average with runners in scoring position",
    valueFormatter: fourDigitNumberFormatter,
  },
];
